// frontend/src/features/tasks/LogCompletionModal.tsx
/**
 * Simple modal for logging a task completion from the All Tasks view.
 * Collects completion date/time and optional notes.
 */
import { useEffect } from 'react';
import {
  Modal,
  Drawer,
  Textarea,
  Button,
  Group,
  Stack,
} from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import { useForm } from '@mantine/form';
import type { TaskDefinition } from '@/types/task';
import { useIsMobile } from '@/hooks/useIsMobile';

export interface LogCompletionData {
  completed_at: string;
  notes?: string;
}

interface LogCompletionModalProps {
  opened: boolean;
  onClose: () => void;
  definition: TaskDefinition | null;
  onSubmit: (data: LogCompletionData) => void;
  isLoading?: boolean;
}

export function LogCompletionModal({
  opened,
  onClose,
  definition,
  onSubmit,
  isLoading,
}: LogCompletionModalProps) {
  const isMobile = useIsMobile();
  const form = useForm<{ completedAt: Date | null; notes: string }>({
    initialValues: {
      completedAt: null,
      notes: '',
    },
  });

  // Reset form when definition changes or modal opens
  useEffect(() => {
    if (definition && opened) {
      form.setValues({
        completedAt: new Date(),
        notes: '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [definition?.id, opened]);

  const handleSubmit = (values: { completedAt: Date | null; notes: string }) => {
    onSubmit({
      completed_at: values.completedAt ? values.completedAt.toISOString() : new Date().toISOString(),
      notes: values.notes || undefined,
    });
  };

  if (!definition) return null;

  const title = `Log Completion: ${definition.name}`;

  const content = (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack>
        <DateTimePicker
          label="Completed at"
          value={form.values.completedAt}
          onChange={(val) => form.setFieldValue('completedAt', val ? new Date(val) : null)}
          popoverProps={{ withinPortal: true }}
        />

        <Textarea
          label="Notes"
          placeholder="Optional notes about this completion"
          {...form.getInputProps('notes')}
        />

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={isLoading}>
            Log Completion
          </Button>
        </Group>
      </Stack>
    </form>
  );

  return isMobile ? (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={title}
      position="bottom"
      size="85%"
      styles={{
        body: {
          paddingBottom: 'env(safe-area-inset-bottom, 16px)',
        },
      }}
    >
      {content}
    </Drawer>
  ) : (
    <Modal
      opened={opened}
      onClose={onClose}
      title={title}
    >
      {content}
    </Modal>
  );
}
